import { logger } from '@hen-site/lib/logger/client';
import {
  MARKETING_CONSENT_POLL_INTERVAL_MS,
  MARKETING_CONSENT_POLL_MAX_ATTEMPTS,
  ONETRUST_GROUP,
  ONETRUST_GROUPS_UPDATED_EVENT,
} from './constants';
import type { MarketingConsentState } from './types';

export interface SubscribeMarketingConsentOptions {
  onPollTimeout?: () => void;
}

/**
 * Returns whether `OnetrustActiveGroups` is present as a string.
 *
 * @returns {boolean} True when the OneTrust groups string exists on window.
 */
const hasGroupsString = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.OnetrustActiveGroups === 'string'
  );
};

/**
 * Parses a comma-delimited OneTrust groups string for the marketing group.
 * Matching is token-exact so a group id cannot match as a substring of another.
 *
 * @param {unknown} raw - Value of `window.OnetrustActiveGroups`.
 * @returns {MarketingConsentState} `granted` only when the marketing group token is present.
 */
const parseActiveGroups = (raw: unknown): MarketingConsentState => {
  if (typeof raw !== 'string') {
    return 'not-granted';
  }

  const tokens = raw
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  if (tokens.includes(ONETRUST_GROUP.ADVERTISING)) {
    return 'granted';
  }

  return 'not-granted';
};

/**
 * Reads the current marketing-consent snapshot. Absent, unparseable, or
 * non-string groups fail closed as `not-granted`.
 *
 * @returns {MarketingConsentState} Current marketing consent state.
 */
export const readMarketingConsent = (): MarketingConsentState => {
  if (typeof window === 'undefined' || !hasGroupsString()) {
    return 'not-granted';
  }

  return parseActiveGroups(window.OnetrustActiveGroups);
};

/**
 * Subscribes to marketing-consent changes. Polls until OneTrust exposes a
 * groups string or the budget expires, and listens for group-update events.
 * Fails closed on timeout. Unsubscribe stops the poll and removes the listener.
 *
 * @param {(state: MarketingConsentState) => void} onChange - Called when consent state is known.
 * @param {SubscribeMarketingConsentOptions} [options] - Optional timeout hook.
 * @returns {() => void} Unsubscribe function.
 */
export const subscribeMarketingConsent = (
  onChange: (state: MarketingConsentState) => void,
  options?: SubscribeMarketingConsentOptions,
): (() => void) => {
  if (typeof window === 'undefined') {
    onChange('not-granted');
    return () => undefined;
  }

  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;

  const emit = (state: MarketingConsentState): void => {
    if (!cancelled) {
      onChange(state);
    }
  };

  const onGroupsUpdated = (): void => {
    emit(readMarketingConsent());
  };

  window.addEventListener(ONETRUST_GROUPS_UPDATED_EVENT, onGroupsUpdated);

  const poll = (): void => {
    if (cancelled) {
      return;
    }

    if (hasGroupsString()) {
      emit(readMarketingConsent());
      return;
    }

    if (attempts >= MARKETING_CONSENT_POLL_MAX_ATTEMPTS) {
      logger.warn(
        'Marketing consent signal did not appear before poll timeout',
      );
      options?.onPollTimeout?.();
      emit('not-granted');
      return;
    }

    attempts += 1;
    timeoutId = setTimeout(poll, MARKETING_CONSENT_POLL_INTERVAL_MS);
  };

  poll();

  return () => {
    cancelled = true;
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    window.removeEventListener(ONETRUST_GROUPS_UPDATED_EVENT, onGroupsUpdated);
  };
};
